import { Form, Modal } from "antd";
import { type Dispatch, type SetStateAction, useState } from "react";
import { useIntl } from "react-intl";
import { UploadImages } from "~components/UploadImages";
import { ImageBaseTypes } from "~enums/imageBaseTypes";
import { ImageTypes } from "~enums/imageTypes";
import { PermissionNames } from "~enums/permissions.ts";
import { useAddImage } from "~hooks/apiHooks/useAddImage";
import { useCreatePerson } from "~hooks/apiHooks/useCreatePerson";
import { AdditionalDetailsSection } from "./components/AdditionalDetailsSection";
import { PersonDetailsSection } from "./components/PersonDetailsSection";
import { SocialSection } from "./components/SocialSection";
import type { AddPersonForm } from "./types/AddPersonForm";
import { useNotification } from "~hooks/useNotification";

type Props = {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  onCreateCb?: (localFullName: string, personId: string) => void;
  closeAfterAdd?: boolean;
};

const imageFor = ImageBaseTypes.Person;
const initImageState = {
  [ImageTypes.PersonHead]: { image: { file: null, url: null }, imageFor },
  [ImageTypes.PersonWaist]: { image: { file: null, url: null }, imageFor },
  [ImageTypes.PersonPose]: { image: { file: null, url: null }, imageFor },
};

export const AddPersonModal = ({ open, setOpen, closeAfterAdd = true, onCreateCb }: Props) => {
  const { formatMessage } = useIntl();

  const [images, setImages] = useState<UploadImages>(initImageState);

  const [form] = Form.useForm<AddPersonForm>();
  const notification = useNotification();

  const { mutateAsync: createPerson, isPending: isFormUploading } = useCreatePerson();
  const { mutateAsync: uploadImage, isPending: isImageUploading } = useAddImage();
  const isLoading = isFormUploading || isImageUploading;

  const onFinish = async ({ additionalDetails, ...values }: AddPersonForm) => {
      const getNumber = (value: string | undefined) =>
        value && value?.length > 0 ? +value.replace(/\.0?0?^/, "") : undefined;

      const height = getNumber(additionalDetails?.height);
      const weight = getNumber(additionalDetails?.weight);

      const isSocialEmpty = Object.values(values.social).every((value) => !value);

      const resetForm = () => {
        form.resetFields();
        setImages(initImageState);
      };

      const { data } = await createPerson({
        ...values,
        ...(values.dob && { dob: values.dob.format("YYYY-MM-DD") }),
        ...(typeof height !== "undefined" || typeof weight !== "undefined" || additionalDetails?.representation
          ? { additionalDetails: { height, weight, representation: additionalDetails?.representation } }
          : {}),
        social: isSocialEmpty ? undefined : values.social,
      });

      const personId = data[0]?.personId;
      if (!personId) throw new Error("Person ID is not defined");

      let isImageUploadError = false;
      try {
        await Promise.all([
          images[ImageTypes.PersonHead]?.image.file &&
          uploadImage({
            baseType: ImageBaseTypes.Person,
            baseId: personId,
            file: images[ImageTypes.PersonHead].image.file,
            imageType: ImageTypes.PersonHead,
            onError: {
              400: () => {
                isImageUploadError = true;
                notification.error({
                  message: formatMessage({ id: "add.person.images.personHead.error" }),
                  placement: "bottomRight"
                });
              }
            }
          }),
          images[ImageTypes.PersonWaist]?.image.file &&
          uploadImage({
            baseType: ImageBaseTypes.Person,
            baseId: personId,
            file: images[ImageTypes.PersonWaist].image.file,
            imageType: ImageTypes.PersonWaist,
            onError: {
              400: () => {
                isImageUploadError = true;
                notification.error({
                  message: formatMessage({ id: "add.person.images.personWaist.error" }),
                  placement: "bottomRight"
                });
              }
            },
          }),
          images[ImageTypes.PersonPose]?.image.file &&
          uploadImage({
            baseType: ImageBaseTypes.Person,
            baseId: personId,
            file: images[ImageTypes.PersonPose].image.file,
            imageType: ImageTypes.PersonPose,
            onError: {
              400: () => {
                isImageUploadError = true;
                notification.error({
                  message: formatMessage({ id: "add.person.images.personPose.error" }),
                  placement: "bottomRight"
                });
              }
            },
          }),
        ]);
      } catch
        (e) {
        console.error(e);
      }

      if (!isImageUploadError) {
        notification.success({
          message: formatMessage({ id: "person.add.success" }),
          placement: "bottomRight",
        });
      }

      if (closeAfterAdd) {
        closeAfterAdd && setOpen(false);
        setTimeout(resetForm, 500); // delay, to reset form after closing animation
      } else {
        resetForm();
      }
      onCreateCb?.(values.nameFullLocal, personId);
    }
  ;

  return (
    <Modal
      open={open}
      onCancel={() => !isLoading && setOpen(false)}
      width={800}
      destroyOnClose
      title={formatMessage({ id: "add.person" })}
      okText={formatMessage({ id: "add" })}
      onOk={form.submit}
      okButtonProps={{ disabled: isLoading, loading: isLoading }}
      cancelButtonProps={{ disabled: isLoading }}
      closable={!isLoading}
    >
      <Form form={form} onFinish={onFinish}>
        <PersonDetailsSection disabled={isLoading} />
        <AdditionalDetailsSection disabled={isLoading} />
        <SocialSection disabled={isLoading} />
        <UploadImages images={images} setImages={setImages} permissionKey={PermissionNames.PERSON} />
      </Form>
    </Modal>
  );
};
